// "Your tags": the company's own tags (list) and the editor with a live
// preview. The preview ranks the tenders by similarity to the explanation;
// the Broad↔Strict slider picks the tag's cutoff among them and is saved as
// minSimilarity. Keyword hits are listed with their snippet, so a noisy
// keyword shows before it is saved. The API calls come in as props.
import React, { useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    List, ListItem, ListItemText, Slider, Stack, TextField, Typography,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

import { draftFromTag, keywordProblem, previewMatches, validateTagDraft, SLIDER_RANGE } from './companyTags';
import type { CompanyTag, TagDraft, TagPreview } from './companyTags';

const PREVIEW_ROWS = 50;

interface Props {
    open: boolean;
    onClose: () => void;
    tags: CompanyTag[];
    matchCounts: Record<string, { total: number; open: number }>;
    tenderInfo: (tenderId: string) => { title: string; open: boolean } | undefined;
    onPreview: (draft: TagDraft) => Promise<TagPreview>;
    onSave: (draft: TagDraft, tagId: string | null) => Promise<void>;
    onDelete: (tagId: string) => Promise<void>;
    isSmallScreen: boolean;
}

const tenders = (n: number) => `${n} tender${n === 1 ? '' : 's'}`;
const textSx = { primaryTypographyProps: { fontSize: '0.8rem' }, secondaryTypographyProps: { fontSize: '0.7rem' } };

const CompanyTagsDialog: React.FC<Props> = ({
    open, onClose, tags, matchCounts, tenderInfo, onPreview, onSave, onDelete, isSmallScreen,
}) => {
    const [editing, setEditing] = useState<{ tagId: string | null } | null>(null);
    const [draft, setDraft] = useState<TagDraft>(draftFromTag(null));
    const [keywordInput, setKeywordInput] = useState('');
    const [preview, setPreview] = useState<TagPreview | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

    const problem = validateTagDraft(draft);

    const runPreview = async (d: TagDraft) => {
        setPreviewing(true);
        setError(null);
        try {
            setPreview(await onPreview(d));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Preview unavailable');
        } finally {
            setPreviewing(false);
        }
    };

    const startEdit = (tag: CompanyTag | null) => {
        const d = draftFromTag(tag);
        setEditing({ tagId: tag?.tagId ?? null });
        setDraft(d);
        setKeywordInput('');
        setPreview(null);
        setError(null);
        if (tag) void runPreview(d);  // an existing tag opens on its current matches
    };

    const addKeyword = () => {
        const keyword = keywordInput.trim();
        if (keyword && !draft.keywords.some(k => k.toLowerCase() === keyword.toLowerCase())) {
            setDraft(d => ({ ...d, keywords: [...d.keywords, keyword] }));
        }
        setKeywordInput('');
    };

    const save = async () => {
        if (!editing || problem) return;
        setBusy(true);
        setError(null);
        try {
            await onSave(draft, editing.tagId);
            setEditing(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not save the tag');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (tagId: string) => {
        setBusy(true);
        setError(null);
        try {
            await onDelete(tagId);
            setConfirmDelete(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not delete the tag');
        } finally {
            setBusy(false);
        }
    };

    const close = () => {
        setEditing(null);
        setConfirmDelete(null);
        setError(null);
        onClose();
    };

    const counts = preview ? previewMatches(preview, draft.minSimilarity, tenderInfo) : null;
    const keywordNote = preview ? keywordProblem(preview.unavailableKeywords, preview.keywordMatchesUnavailable) : null;

    const previewPanel = preview && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            {preview.candidates.length > 0 && (
                <Box sx={{ px: 1 }}>
                    <Typography variant="caption" color="text.secondary">Cutoff</Typography>
                    <Slider size="small" min={SLIDER_RANGE[0]} max={SLIDER_RANGE[1]} step={0.01}
                        value={draft.minSimilarity}
                        onChange={(_e, v) => setDraft(d => ({ ...d, minSimilarity: v as number }))}
                        marks={[{ value: SLIDER_RANGE[0], label: 'Broad' }, { value: SLIDER_RANGE[1], label: 'Strict' }]}
                        valueLabelDisplay="auto" valueLabelFormat={(v) => `${Math.round(v * 100)} %`}
                        aria-label="Cutoff" />
                </Box>
            )}
            {counts && (
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {tenders(counts.total)} match{counts.total === 1 ? 'es' : ''} ({counts.open} open)
                </Typography>
            )}
            {keywordNote && <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{keywordNote}</Alert>}
            <List dense disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {preview.keywordHits.filter(h => tenderInfo(h.tenderId)).map(h => (
                    <ListItem key={`kw-${h.tenderId}-${h.keyword}`} disableGutters>
                        <ListItemText {...textSx}
                            primary={tenderInfo(h.tenderId)!.title}
                            secondary={`found “${h.keyword}” in ${h.source === 'title' ? 'the title' : h.source}`
                                + (h.snippet ? ` — …${h.snippet.replace(/\s+/g, ' ').trim()}…` : '')} />
                    </ListItem>
                ))}
                {preview.candidates.filter(c => tenderInfo(c.tenderId)).slice(0, PREVIEW_ROWS).map(c => {
                    const info = tenderInfo(c.tenderId)!;
                    return (
                        <ListItem key={c.tenderId} disableGutters
                            sx={{ opacity: c.similarity < draft.minSimilarity ? 0.4 : 1 }}>
                            <ListItemText {...textSx} primary={info.title}
                                secondary={`${Math.round(c.similarity * 100)} % similar · ${info.open ? 'open' : 'closed'}`} />
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );

    const editor = (
        <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField label="Name" size="small" value={draft.name}
                onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} />
            <TextField label="Explanation" size="small" multiline minRows={2} value={draft.explanation}
                onChange={(e) => setDraft(d => ({ ...d, explanation: e.target.value }))}
                helperText="What kind of work this tag stands for. Matched by meaning, in any language." />
            <Box>
                {draft.keywords.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                        {draft.keywords.map(k => (
                            <Chip key={k} size="small" label={k}
                                onDelete={() => setDraft(d => ({ ...d, keywords: d.keywords.filter(x => x !== k) }))} />
                        ))}
                    </Box>
                )}
                <TextField label="Exact keywords" size="small" fullWidth value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addKeyword();
                        }
                    }}
                    onBlur={addKeyword}
                    helperText="Product names or phrases, found word for word in the documents, e.g. Tableau Server. Enter adds one." />
            </Box>
            <Box>
                <Button variant="outlined" size="small" disabled={!!problem || previewing}
                    onClick={() => runPreview(draft)} sx={{ textTransform: 'none' }}>
                    {previewing ? 'Previewing…' : 'Preview matches'}
                </Button>
            </Box>
            {previewPanel}
            {problem && <Typography variant="caption" color="text.secondary">{problem}</Typography>}
        </Stack>
    );

    const list = (
        <Stack spacing={1} sx={{ pt: 1 }}>
            {tags.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Tags mark the tenders that fit what your company cares about: a kind of work
                    (“BI & dashboards: dashboards, data visualization and reporting systems”) or a
                    product (“Tableau” with the keyword Tableau Server). They label and filter the
                    list and never change scores.
                </Typography>
            )}
            {tags.map(tag => {
                const c = matchCounts[tag.tagId] ?? { total: 0, open: 0 };
                return (
                    <Box key={tag.tagId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalOfferOutlinedIcon fontSize="small" color="secondary" />
                            <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{tag.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {tenders(c.total)} ({c.open} open)
                            </Typography>
                        </Box>
                        {tag.explanation && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tag.explanation}</Typography>
                        )}
                        {tag.keywords.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {tag.keywords.map(k => <Chip key={k} size="small" variant="outlined" label={k} />)}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                            <Typography variant="caption" color="text.disabled" sx={{ flexGrow: 1 }}>
                                {tag.updatedBy ? `Last changed by ${tag.updatedBy}` : ''}
                            </Typography>
                            {confirmDelete === tag.tagId ? (
                                <>
                                    <Typography variant="caption">Delete “{tag.name}”?</Typography>
                                    <Button size="small" onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
                                    <Button size="small" color="error" onClick={() => remove(tag.tagId)} disabled={busy}>Delete</Button>
                                </>
                            ) : (
                                <>
                                    <Button size="small" onClick={() => startEdit(tag)}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => setConfirmDelete(tag.tagId)}>Delete</Button>
                                </>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle>{editing ? (editing.tagId ? 'Edit tag' : 'New tag') : 'Your tags'}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {editing ? editor : list}
            </DialogContent>
            <DialogActions>
                {editing ? (
                    <>
                        <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
                        <Button variant="contained" onClick={save} disabled={busy || !!problem}>
                            {busy ? <CircularProgress size={18} /> : 'Save'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button startIcon={<LocalOfferOutlinedIcon />} onClick={() => startEdit(null)}>New tag</Button>
                        <Button onClick={close}>Close</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CompanyTagsDialog;
